import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { fetchPasses, selectPassesByContext, selectPassesByIds } from "./slice";
import { parsePassesFilters } from "./filters";
import { updateSearchParamsWithFilters } from "../filters/filters";
import Layout from "../../ui/Layout";
import NavMain from "../../app/NavMain";
import ScrollablePage from "../../ui/ScrollablePage";
import Paper from "../../ui/Paper";
import ErrorMsg from "../../ui/ErrorMsg";
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner";
import PassesTable from "./PassesTable";
import TextFilter from "../filters/TextFilter";
import PropTypes from "prop-types";
import { Action, Actions, Header, Overview } from "../../ui/PageHeader";
import Button from "../../ui/Button";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { API_BASE_URL } from "../../app/config";
import separateStrings from "../../strings/stringSplitter";
import LoadingSpinnerInline from "../../ui/LoadingSpinnerInline";
import { selectAuth } from "../iam/slice";

const PAGE_SIZE = 50; // Default initial and subsequent page size

/* PassesList renders a page containing a filter- and sort-able table
 * of all data passes matching the criteria in the current URL.  */
const PassesList = () => {
	const dispatch = useDispatch();
	const [selectedPasses, setSelectedPasses] = useState([]);
	const { token } = useSelector(selectAuth);

	// we get any matching data passes
	const passesMatches = useSelector((state) =>
		selectPassesByContext(state, "root"),
	);
	// We get the full objects from the ids that match
	const passes = useSelector((state) =>
		selectPassesByIds(state, passesMatches?.ids || []),
	);

	// Parse parameters into filter object (which drives filters UI state)
	// Note: We are using the URL as the "state" here, and updates
	// perform a history replacement (so as not to mess up "back" button
	// behaviour too much - though we may want to apply a heuristic like
	// small changes replace history, big changes push history)
	const location = useLocation();

	const navigate = useNavigate();
	const urlParams = new URLSearchParams(location.search);
	const filter = parsePassesFilters(urlParams);
	updateSearchParamsWithFilters(urlParams, filter);

	useEffect(() => {
		dispatch(
			fetchPasses({
				filter,
				page: { offset: 0, limit: PAGE_SIZE },
				context: ["root"],
			}),
		);
	}, [urlParams.toString()]);

	// How we apply a filter update. Filter state is entirely in the URL query parameters
	const setFilter = (filter = {}) => {
		const updUrlParams = updateSearchParamsWithFilters(urlParams, filter);
		navigate({
			pathname: location.pathname,
			search: `?${updUrlParams}`,
		});
	};

	// How many more passes are available to load?
	const loadableCount = Math.min(
		PAGE_SIZE,
		passes?.length && passesMatches?.page?.total_count
			? passesMatches.page.total_count - passes.length
			: 50,
	);

	// Event handler for 'Show xxx more' button that was clicked
	const handleLoadMore = () => {
		const lastIndex = passes?.length;

		const page = {
			offset: lastIndex,
			limit: loadableCount,
		};
		dispatch(fetchPasses({ filter, page, context: ["root"] }));
	};

	const toggleSelectedPass = (priority) => {
		if (selectedPasses.includes(priority)) {
			setSelectedPasses(selectedPasses.filter((p) => p !== priority));
		} else {
			setSelectedPasses([...selectedPasses, priority]);
		}
	};

	function downloadPassesCSVFile(passIds) {
		// Create a temporary link element
		const link = document.createElement("a");
		link.href = `${API_BASE_URL}/api/passes/data?pass_ids=${passIds}&sort_by=start_date&sort_order=ASC${
			token ? "&token=" + encodeURIComponent(token) : ""
		}`;
		link.target = "_blank"; // Open the download in a new tab

		// Trigger the download
		link.click();
	}

	return (
		<Layout>
			<NavMain key="nav-left" />
			<Helmet>
				<title>Passes - Astrosite</title>
			</Helmet>
			<ScrollablePage>
				{passes.length === 0 && passesMatches?._loadStatus === "loading" && (
					<Backdrop>
						<LoadingSpinner />
					</Backdrop>
				)}
				<Header>
					<Overview>
						<h1>RSO Passes</h1>
						<p>
							{passes.length > 0 && (
								<span>
									{passesMatches?._loadStatus === "loading" && (
										<LoadingSpinnerInline />
									)}
									Showing <strong>{passesMatches?.page?.end_index}</strong>
									{passesMatches?.page?.total_count && (
										<span>
											{" "}
											of{" "}
											<strong className="count total_count">
												{passesMatches.page.total_count}
											</strong>
										</span>
									)}{" "}
									passes
								</span>
							)}
						</p>
					</Overview>
					<Actions>
						<Action>
							<Button
								icon={faDownload}
								onClick={() =>
									downloadPassesCSVFile(selectedPasses?.map((p) => p.id))
								}
								disabled={!selectedPasses || selectedPasses.length === 0}
							>
								Export {selectedPasses?.length} as CSV
							</Button>
						</Action>
					</Actions>
				</Header>
				{passesMatches?._loadStatus == "rejected" && (
					<ErrorMsg
						msg={"There was a problem fetching the passes"}
						showIcon={true}
					/>
				)}
				<Header>
					<ErrorMsg
						msg="Please note: The Passes feature is currently unavailable while multi-modal telescope operation support is added"
						showIcon
					/>
				</Header>
				<Paper>
					<NoradIdFilter filter={filter} setFilter={setFilter} />
					<PassesTable
						passes={passes}
						filter={filter}
						setFilter={setFilter}
						selectedPasses={selectedPasses}
						toggleSelectedPass={toggleSelectedPass}
						setSelectedPasses={setSelectedPasses}
					/>
					{passes?.length == 0 && passesMatches?._loadStatus == "idle" && (
						<p>There are no passes matching your criteria.</p>
					)}
					<footer className="actions">
						{passes.length > 0 &&
							passes.length !== passesMatches?.page?.total_count && (
								<>
									<button
										onClick={handleLoadMore}
										disabled={passesMatches?._loadStatus == "loading"}
									>
										{passesMatches?._loadStatus == "loading"
											? "Loading..."
											: `Show ${loadableCount} more`}
									</button>
								</>
							)}
					</footer>
				</Paper>
			</ScrollablePage>
		</Layout>
	);
};

/**
 * Split the string bases on the supported separators (,:; ), remove any duplicates and return as an array
 * @param idsString
 * @returns {T[]}
 */
function splitAndRemoveDuplicates(idsString) {
	// Step 1: Split the comma-separated string into an array of IDs
	// Step 2: Convert to a set to remove duplicates then convert back
	return Array.from(
		new Set(separateStrings(idsString, ",.;: ").filter((id) => id != "")),
	);
}

const NoradIdFilter = ({ filter = {}, setFilter }) => {
	const updateParentFilter = (filter = {}) => {
		setFilter(filter);
	};

	return (
		<div>
			<div>
				Enter a list of norad ids separated by commas or spaces eg. 53763, 57176
			</div>
			<TextFilter
				filterByName="object_ids"
				filter={filter}
				setFilter={updateParentFilter}
				active={true}
				transform={(v) => splitAndRemoveDuplicates(v).join(",")}
			/>
		</div>
	);
};

NoradIdFilter.propTypes = {
	filter: PropTypes.object,
	setFilter: PropTypes.func,
};

export default PassesList;
